import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {LocalStorage} from '../../utils/LocalStorage';
import {isOk} from '../../utils/apiResponse';
import {UI_VERSION_V2, useUiVersion, useUserModules} from '../../context/UserModulesContext';
import {CENTER_KEYS_BY_ROW, UserEmailAlerts} from './UserEmailAlerts';

// La ruta del Centro, escrita acá: el clásico no importa nada de `alerts_center/` (`classicIsolation.test.js`).
const CENTER_RULES_PATH = '/alerts/rules';

// Por familia: una hermana para unas líneas (`BUDGET_1@90`) es de la familia `BUDGET_1` (review C-av2-fe-centro-02).
function matchesKey(systemKey, key) {
    const text = String(systemKey || '');
    return key.endsWith(':') ? text.startsWith(key) : text.split('@')[0] === key;
}

/**
 * Por fila de «Mis alertas», si el Centro se la manda por correo a quien mira: `true`/`false`, o `null` si quien
 * mira no es el dueño de la pantalla (`reaches_viewer` habla de quien pide la lista). Una fila sin alerta en el
 * Centro (una sugerida que nadie agregó) no le llega a nadie.
 */
export function reachesByRow(list, {isViewer}) {
    const rows = [...((list && list.rules) || []), ...((list && list.controls) || [])].filter((rule) => !rule.deleted_on);
    const result = {};
    Object.entries(CENTER_KEYS_BY_ROW).forEach(([rowKey, keys]) => {
        if (!isViewer || !list) {
            result[rowKey] = null;
            return;
        }
        const matching = rows.filter((rule) => keys.some((key) => matchesKey(rule.system_key, key)));
        result[rowKey] = matching.some((rule) => {
            const delivery = rule.delivery || {};
            return (delivery.channels || []).includes('EMAIL') && delivery.reaches_viewer === true;
        });
    });
    return result;
}

/**
 * «Mis alertas» del usuario (clásico) con el Centro de Alertas v2: en una agencia con las alertas convertidas,
 * los tipos que ahora se eligen en Alertas se leen (si le llegan, según el Centro) con el link a Alertas; los
 * informes siguen como siempre. El link pasa a la 2.1 (el Centro existe sólo ahí), como el aviso del clásico de
 * sensibilidades (`AlertRulesNote`).
 *
 * Sin el Centro, o sin convertir, monta la pantalla de siempre. La lista de reglas se pide sólo con el Centro
 * prendido en la agencia; si el pedido falla, la pantalla queda como siempre.
 */
export function UserEmailAlertsCenter(props) {
    const {alertRulesEnabled, systemAlertsConverted} = useUserModules();
    const {setUiVersion} = useUiVersion();
    const [list, setList] = useState(null);

    useEffect(() => {
        if (alertRulesEnabled !== true) return undefined;
        let cancelled = false;
        new APIClient(`${API_URL}agency/alert-rules/`).call('GET').then((response) => {
            if (!cancelled && isOk(response) && response.result) setList(response.result);
        });
        return () => { cancelled = true; };
    }, [alertRulesEnabled]);

    const converted = alertRulesEnabled === true
        && (systemAlertsConverted === true || Boolean(list && list.system_alerts_converted === true));
    if (!converted) return <UserEmailAlerts {...props}/>;

    const viewerId = LocalStorage.fetchUserId();
    const centerAlerts = {
        reachesByRow: reachesByRow(list, {isViewer: Boolean(viewerId) && String(viewerId) === String(props.userId)}),
        link: (text) => (
            <Link to={CENTER_RULES_PATH} className="user-email-alerts__moved-link" onClick={() => setUiVersion(UI_VERSION_V2)}>
                {text}
            </Link>
        ),
    };
    return <UserEmailAlerts {...props} centerAlerts={centerAlerts}/>;
}

export default UserEmailAlertsCenter;
